import { Download, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import type { DownloadProgress } from '@/types/download';

interface SelectionSummaryProps {
  totalActivities: number;
  totalPatients: number;
  onDownload: () => void;
  isDownloading: boolean;
  progress?: DownloadProgress;
}

export function SelectionSummary({
  totalActivities,
  totalPatients,
  onDownload,
  isDownloading,
  progress,
}: SelectionSummaryProps) {
  const canDownload = totalActivities > 0;

  const getProgressText = () => {
    if (!progress) return null;

    switch (progress.status) {
      case 'fetching':
        return `Fetching activities... ${progress.current}/${progress.total}`;
      case 'packaging':
        return 'Creating ZIP file...';
      case 'complete':
        return 'Download complete!';
      case 'error':
        return progress.message || 'An error occurred';
      default:
        return null;
    }
  };

  const progressText = getProgressText();
  const progressPercentage = progress && progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  return (
    <Card>
      <CardBody className="flex flex-col items-center gap-4 sm:flex-row sm:justify-between">
        <div className="text-center sm:text-left">
          <p className="text-lg font-semibold text-foreground">
            {totalActivities.toLocaleString()} {totalActivities === 1 ? 'activity' : 'activities'} selected
            {' '}from {totalPatients} {totalPatients === 1 ? 'patient' : 'patients'}
          </p>
          {progressText && (
            <div className="mt-2">
              <p className="text-sm text-muted">{progressText}</p>
              {progress?.status === 'fetching' && (
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${progressPercentage}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {progress?.status === 'error' && (
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">Failed</span>
            </div>
          )}
          <Button
            size="lg"
            onClick={onDownload}
            isLoading={isDownloading}
            disabled={!canDownload || isDownloading}
            leftIcon={<Download className="h-5 w-5" />}
          >
            Download ZIP
          </Button>
        </div>
      </CardBody>
    </Card>
  );
}
